import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {
  listConversations,
  listMessages,
  sendMessage,
  sendAttachment,
  markRead,
  updateConversation,
  listContacts,
  getStats,
  searchAccounts,
  mediaUrl,
  getContext,
  loginAsCustomer,
  openTicket,
  suggestReplies,
  listAgentActions,
  getAgentLog,
  runAgentAction,
  revertAgentAction,
  WaContext,
  AgentActionDef,
  AgentActionRecord,
  SuggestedAgentAction,
  WaConversation,
  WaMessage,
  WaStats,
  WaFilter,
  WaAccount,
} from './_requests'

const err = (e: any, fallback = 'שגיאה') => e?.response?.data?.error || e?.message || fallback

// How often the inbox re-polls. There is no push channel from the provider to
// the browser, so the list and the open thread refresh on a timer.
const POLL_MS = 10000

const accountName = (a?: WaAccount | null) =>
  !a ? '' : (a.company_name || [a.first_name, a.last_name].filter(Boolean).join(' ') || a.email || a.username || '').trim()

// A shared contact ("contacts" message) arrives as the provider's JSON array:
// [{name: {formatted_name, ...}, phones: [{phone, wa_id, type}], ...}].
type SharedContact = {name: string; phones: {phone: string; wa_id?: string; type?: string}[]}
const parseSharedContacts = (body?: string): SharedContact[] => {
  try {
    const arr = JSON.parse(body || '')
    if (!Array.isArray(arr)) return []
    return arr.map((c: any) => ({
      name:
        c?.name?.formatted_name ||
        [c?.name?.first_name, c?.name?.last_name].filter(Boolean).join(' ') ||
        c?.org?.company ||
        'איש קשר',
      phones: Array.isArray(c?.phones) ? c.phones.filter((p: any) => p && (p.phone || p.wa_id)) : [],
    }))
  } catch {
    return []
  }
}

// The list preview for a thread whose last message was a shared contact is
// that raw JSON — show the name instead.
const previewText = (t?: string) => {
  if (t && t.startsWith('[{') && t.includes('"name"')) {
    const m = /"formatted_name":"((?:[^"\\]|\\.)*)"/.exec(t)
    if (m) {
      try {
        return '👤 ' + JSON.parse('"' + m[1] + '"')
      } catch {}
    }
    return '👤 איש קשר'
  }
  return t
}

// "14:32" for today, "אתמול", else a short date — the way a chat list dates rows.
const listTime = (d?: string | null) => {
  if (!d) return ''
  const x = new Date(d)
  if (isNaN(x.getTime())) return ''
  const now = new Date()
  const sameDay = x.toDateString() === now.toDateString()
  if (sameDay) return x.toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'})
  const yest = new Date(now)
  yest.setDate(now.getDate() - 1)
  if (x.toDateString() === yest.toDateString()) return 'אתמול'
  return x.toLocaleDateString('he-IL', {day: '2-digit', month: '2-digit', year: '2-digit'})
}
const bubbleTime = (d?: string | null) => {
  if (!d) return ''
  const x = new Date(d)
  return isNaN(x.getTime()) ? '' : x.toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'})
}
const dayLabel = (d?: string | null) => {
  if (!d) return ''
  const x = new Date(d)
  if (isNaN(x.getTime())) return ''
  const now = new Date()
  if (x.toDateString() === now.toDateString()) return 'היום'
  const yest = new Date(now)
  yest.setDate(now.getDate() - 1)
  if (x.toDateString() === yest.toDateString()) return 'אתמול'
  return x.toLocaleDateString('he-IL', {day: '2-digit', month: '2-digit', year: 'numeric'})
}

// 0522138367 reads better than 972522138367 for an Israeli number.
const prettyPhone = (p: string) => {
  const d = String(p || '').replace(/\D/g, '')
  const local = d.indexOf('972') === 0 ? '0' + d.slice(3) : d
  return local.length === 10 ? `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}` : local
}

const initials = (c: WaConversation) => {
  const n = (c.name || '').trim()
  if (n) return n.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
  return prettyPhone(c.phone).slice(-2)
}

// Outgoing status, as WhatsApp's ticks read.
const statusMark = (m: WaMessage) => {
  if (m.direction !== 'out') return ''
  switch (m.status) {
    case 'read': return '✓✓'
    case 'delivered': return '✓✓'
    case 'sent': return '✓'
    case 'failed': return '⚠'
    default: return '🕘'
  }
}
const statusColor = (m: WaMessage) =>
  m.status === 'read' ? '#53bdeb' : m.status === 'failed' ? '#d92d20' : '#8696a0'

const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e6e9f0',
  padding: 18,
  marginBottom: 16,
}
const chip = (bg: string, color = '#fff'): React.CSSProperties => ({
  background: bg,
  color,
  borderRadius: 999,
  padding: '2px 10px',
  fontSize: 12,
  fontWeight: 700,
  display: 'inline-block',
})

// How an agent action's fields read to a person: "פרסום אוטומטי: לא → כן".
const FIELD_LABELS: Record<string, string> = {
  autopost_enabled: 'פרסום אוטומטי',
  blocked_until: 'חסימת קצב עד',
  blacklisted_groups: 'קבוצות חסומות',
  enabled: 'קמפיין פעיל',
  marketplace_enabled: 'מרקטפלייס',
}
const fmtVal = (v: any): string => {
  if (v === null || v === undefined) return '—'
  if (v === true) return 'כן'
  if (v === false) return 'לא'
  if (Array.isArray(v)) return `${v.length} קבוצות`
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleString('he-IL')
  return String(v)
}
// One "field: before → after" line per field an action writes.
const changeLines = (before: any, after: any, absent: string[] = []): string[] =>
  Object.keys(after || {}).map((f) => {
    const was = absent.includes(f) ? null : before ? before[f] : null
    return `${FIELD_LABELS[f] || f}: ${fmtVal(was)} ← ${fmtVal(after[f])}`
  })

const WhatsappPage: FC = () => {
  // The open conversation and tab live in the URL, so a refresh, a bookmark or
  // a link pasted to someone else reopens the same chat instead of an empty pane.
  const [searchParams, setSearchParams] = useSearchParams()
  const urlConv = searchParams.get('c') || ''
  const urlTab = searchParams.get('tab') === 'contacts' ? 'contacts' : 'inbox'

  // Derived from the URL rather than mirrored into state, so there is exactly one
  // source of truth — no effect syncing two copies and fighting over which wins.
  const tab = urlTab
  const setTab = useCallback(
    (next: 'inbox' | 'contacts') => {
      const p = new URLSearchParams(searchParams)
      if (next === 'contacts') p.set('tab', 'contacts')
      else p.delete('tab')
      setSearchParams(p, {replace: true})
    },
    [searchParams, setSearchParams]
  )

  const [stats, setStats] = useState<WaStats | null>(null)
  const [error, setError] = useState('')

  const [convs, setConvs] = useState<WaConversation[]>([])
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<WaFilter>('all')
  const [loadingList, setLoadingList] = useState(true)

  // Opening a thread is a real navigation: it pushes, so Back returns to the
  // thread you came from and a refresh or a pasted link lands on the same chat.
  const activeId = urlConv
  const setActiveId = useCallback(
    (id: string) => {
      const p = new URLSearchParams(searchParams)
      if (id) p.set('c', id)
      else p.delete('c')
      p.delete('tab') // opening a chat always lands on the inbox tab
      setSearchParams(p)
    },
    [searchParams, setSearchParams]
  )
  const [active, setActive] = useState<WaConversation | null>(null)
  const [messages, setMessages] = useState<WaMessage[]>([])
  const [loadingThread, setLoadingThread] = useState(false)
  const [canSend, setCanSend] = useState(false)

  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)

  // AI suggestions live only until the thread changes — a draft for one customer
  // must never be sitting in the box when you open the next one.
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [aiSummary, setAiSummary] = useState('')
  const [suggesting, setSuggesting] = useState(false)
  const [aiDiagnosis, setAiDiagnosis] = useState('')
  // A file picked to send; the text box becomes its caption.
  const [attachFile, setAttachFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Drag-and-drop onto the open chat. A counter, not a boolean: dragenter/leave
  // fire for every child the pointer crosses, so a flag flickers off mid-drag.
  const [dragDepth, setDragDepth] = useState(0)
  // One object URL per picked image (not one per render), released when it changes.
  const attachPreview = useMemo(
    () => (attachFile && attachFile.type.startsWith('image/') ? URL.createObjectURL(attachFile) : ''),
    [attachFile]
  )
  useEffect(() => () => { if (attachPreview) URL.revokeObjectURL(attachPreview) }, [attachPreview])
  // An agent action the AI proposed, shown with its exact before/after. It only
  // runs when an admin presses "אשר" — and then it is logged and undoable.
  const [suggestedAction, setSuggestedAction] = useState<SuggestedAgentAction | null>(null)
  // Which customer message the suggestions were drafted for, so a thread is only
  // auto-drafted once per new inbound message (re-opening it doesn't pay again).
  const autoSuggestedFor = useRef<Set<string>>(new Set())
  const activeIdRef = useRef('')

  // The agent panel. Nothing here runs on its own — an admin picks an action and
  // a target and presses the button, and every run lands in the log below it.
  const [agentOpen, setAgentOpen] = useState(false)
  const [agentDefs, setAgentDefs] = useState<AgentActionDef[]>([])
  const [agentLog, setAgentLog] = useState<AgentActionRecord[]>([])
  const [agentAction, setAgentAction] = useState('')
  const [agentTarget, setAgentTarget] = useState('')
  const [agentBusy, setAgentBusy] = useState(false)

  const [context, setContext] = useState<WaContext | null>(null)
  const [ticketOpen, setTicketOpen] = useState(false)
  const [ticketSubject, setTicketSubject] = useState('')
  const [ticketBody, setTicketBody] = useState('')
  const [creatingTicket, setCreatingTicket] = useState(false)
  const [lastTicket, setLastTicket] = useState('')

  const [showInfo, setShowInfo] = useState(false)
  const [notesDraft, setNotesDraft] = useState('')
  const [acctQuery, setAcctQuery] = useState('')
  const [acctResults, setAcctResults] = useState<WaAccount[]>([])

  const [contacts, setContacts] = useState<WaConversation[]>([])
  const [contactSearch, setContactSearch] = useState('')
  const [loadingContacts, setLoadingContacts] = useState(false)

  const bottomRef = useRef<HTMLDivElement | null>(null)
  // Scroll to the newest message only when the thread changes or it grows —
  // not on every poll, which would yank the view while you read history.
  const lastCountRef = useRef(0)

  const loadStats = useCallback(async () => {
    try {
      const {data} = await getStats()
      setStats(data)
      setCanSend(!!data.can_send)
    } catch (e) {
      /* the header counters are decoration — never block the inbox on them */
    }
  }, [])

  const loadList = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoadingList(true)
      try {
        const {data} = await listConversations({search: search.trim(), filter, limit: 100})
        setConvs(data.conversations || [])
        setCanSend(!!data.can_send)
        setError('')
      } catch (e) {
        setError(err(e, 'טעינת השיחות נכשלה'))
      } finally {
        setLoadingList(false)
      }
    },
    [search, filter]
  )

  // Defined before the effect that calls it — a const is not hoisted, and the
  // production build rejects the use-before-declaration the dev server tolerated.
  const loadAgentLog = useCallback(async (id: string) => {
    try {
      const {data} = await getAgentLog(id)
      setAgentLog(data.actions || [])
    } catch (e) {
      setAgentLog([])
    }
  }, [])

  const loadThread = useCallback(
    async (id: string, quiet = false) => {
      if (!id) return
      if (!quiet) setLoadingThread(true)
      try {
        const {data} = await listMessages(id, {limit: 200})
        setActive(data.contact)
        setMessages(data.messages || [])
        setCanSend(!!data.can_send)
        if (!quiet) setNotesDraft(data.contact?.notes || '')
      } catch (e) {
        setError(err(e, 'טעינת השיחה נכשלה'))
      } finally {
        setLoadingThread(false)
      }
    },
    []
  )

  useEffect(() => {
    loadStats()
  }, [loadStats])

  // Debounced so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => loadList(), search ? 350 : 0)
    return () => clearTimeout(t)
  }, [loadList, search])

  useEffect(() => {
    if (!activeId) return
    loadThread(activeId)
    markRead(activeId).catch(() => {})
    setConvs((prev) => prev.map((c) => (c._id === activeId ? {...c, unread_count: 0} : c)))
    lastCountRef.current = 0

    // Everything thread-scoped resets, so nothing leaks between customers.
    setSuggestions([])
    setAiSummary('')
    setAiDiagnosis('')
    setSuggestedAction(null)
    setAttachFile(null)
    setDraft('')
    setTicketOpen(false)
    setLastTicket('')
    setContext(null)
    setAgentOpen(false)
    setAgentAction('')
    setAgentTarget('')
    getContext(activeId)
      .then(({data}) => setContext(data))
      .catch(() => setContext(null))
    loadAgentLog(activeId)
  }, [activeId, loadThread, loadAgentLog])

  // The action list is fixed server-side; fetch it once.
  useEffect(() => {
    listAgentActions()
      .then(({data}) => setAgentDefs(data.actions || []))
      .catch(() => setAgentDefs([]))
  }, [])

  // One timer drives both panes; quiet refreshes keep the UI from flickering.
  useEffect(() => {
    const t = setInterval(() => {
      if (tab === 'inbox') {
        loadList(true)
        if (activeId) loadThread(activeId, true)
      }
      loadStats()
    }, POLL_MS)
    return () => clearInterval(t)
  }, [tab, activeId, loadList, loadThread, loadStats])

  useEffect(() => {
    if (messages.length !== lastCountRef.current) {
      lastCountRef.current = messages.length
      bottomRef.current?.scrollIntoView({behavior: 'auto'})
    }
  }, [messages])

  const loadContacts = useCallback(async () => {
    setLoadingContacts(true)
    try {
      const {data} = await listContacts({search: contactSearch.trim(), limit: 300})
      setContacts(data.contacts || [])
    } catch (e) {
      setError(err(e, 'טעינת אנשי הקשר נכשלה'))
    } finally {
      setLoadingContacts(false)
    }
  }, [contactSearch])

  useEffect(() => {
    if (tab !== 'contacts') return
    const t = setTimeout(() => loadContacts(), contactSearch ? 350 : 0)
    return () => clearTimeout(t)
  }, [tab, loadContacts, contactSearch])

  useEffect(() => {
    if (acctQuery.trim().length < 2) {
      setAcctResults([])
      return
    }
    const t = setTimeout(async () => {
      try {
        const {data} = await searchAccounts(acctQuery.trim())
        setAcctResults(data.accounts || [])
      } catch (e) {
        setAcctResults([])
      }
    }, 300)
    return () => clearTimeout(t)
  }, [acctQuery])

  const doSend = async () => {
    const text = draft.trim()
    if ((!text && !attachFile) || !activeId || sending) return
    setSending(true)
    try {
      const {data} = attachFile ? await sendAttachment(activeId, attachFile, text) : await sendMessage(activeId, text)
      setMessages((prev) => [...prev, data.message])
      setDraft('')
      setAttachFile(null)
      if (!data.ok) setError(data.error || 'השליחה נכשלה')
      else setError('')
      loadList(true)
    } catch (e) {
      setError(err(e, 'השליחה נכשלה'))
    } finally {
      setSending(false)
    }
  }

  // --- Agent actions. Admin-triggered, recorded, undoable. ---
  const doRunAgent = async () => {
    if (!activeId || !agentAction || agentBusy) return
    const def = agentDefs.find((d) => d.key === agentAction)
    if (def && def.target !== 'account' && !agentTarget) {
      setError('יש לבחור יעד לפעולה')
      return
    }
    setAgentBusy(true)
    try {
      const {data} = await runAgentAction(activeId, agentAction, agentTarget || undefined)
      if (!data.ok) setError(data.error || 'הפעולה נכשלה')
      else setError('')
      await loadAgentLog(activeId)
      getContext(activeId).then(({data: c}) => setContext(c)).catch(() => {})
    } catch (e) {
      setError(err(e, 'הפעולה נכשלה'))
    } finally {
      setAgentBusy(false)
    }
  }

  const doRevertAgent = async (actionId: string) => {
    if (agentBusy) return
    setAgentBusy(true)
    try {
      const {data} = await revertAgentAction(actionId)
      if (!data.ok) setError(data.error || 'הביטול נכשל')
      else setError('')
      if (activeId) {
        await loadAgentLog(activeId)
        getContext(activeId).then(({data: c}) => setContext(c)).catch(() => {})
      }
    } catch (e) {
      setError(err(e, 'הביטול נכשל'))
    } finally {
      setAgentBusy(false)
    }
  }

  // --- AI drafting. Fills the composer; never sends. ---
  const doSuggest = useCallback(async () => {
    const forId = activeIdRef.current
    if (!forId) return
    setSuggesting(true)
    setSuggestions([])
    setAiSummary('')
    setAiDiagnosis('')
    setSuggestedAction(null)
    try {
      const {data} = await suggestReplies(forId)
      // The admin may have moved to another chat while the AI was thinking.
      if (activeIdRef.current !== forId) return
      if (!data.ok) {
        setError(data.error || 'ה-AI לא הצליח להציע תשובה')
      } else {
        setSuggestions(data.suggestions || [])
        setAiSummary(data.summary || '')
        setAiDiagnosis(data.diagnosis || '')
        setSuggestedAction(data.suggested_action || null)
        setError('')
      }
    } catch (e) {
      if (activeIdRef.current === forId) setError(err(e, 'ה-AI לא הצליח להציע תשובה'))
    } finally {
      if (activeIdRef.current === forId) setSuggesting(false)
    }
  }, [])

  useEffect(() => {
    activeIdRef.current = activeId || ''
  }, [activeId])

  // Suggestions appear on their own when a chat is open and the customer spoke
  // last — once per new inbound message. They only ever fill the composer.
  const lastMsg = messages.length ? messages[messages.length - 1] : null
  useEffect(() => {
    if (!activeId || loadingThread || !lastMsg || lastMsg.direction !== 'in') return
    const key = activeId + ':' + lastMsg._id
    if (autoSuggestedFor.current.has(key)) return
    autoSuggestedFor.current.add(key)
    doSuggest()
  }, [activeId, loadingThread, lastMsg, doSuggest])

  // Alt+1..3 drops the matching suggestion into the composer (still not sent).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey) return
      const n = ['Digit1', 'Digit2', 'Digit3'].indexOf(e.code)
      if (n === -1 || !suggestions[n]) return
      e.preventDefault()
      setDraft(suggestions[n])
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [suggestions])

  // Confirming the AI's proposed action: the same logged, undoable path as the
  // manual agent panel, which then opens so the new row (and its "בטל") is visible.
  const doApplySuggestedAction = async () => {
    if (!activeId || !suggestedAction || agentBusy) return
    setAgentBusy(true)
    try {
      const {data} = await runAgentAction(activeId, suggestedAction.action, suggestedAction.target_id)
      if (!data.ok) {
        setError(data.error || 'הפעולה נכשלה')
      } else {
        setError('')
        setSuggestedAction(null)
        setAgentOpen(true)
      }
      await loadAgentLog(activeId)
      getContext(activeId).then(({data: c}) => setContext(c)).catch(() => {})
    } catch (e) {
      setError(err(e, 'הפעולה נכשלה'))
    } finally {
      setAgentBusy(false)
    }
  }

  // Opens the customer's own account in a new tab, as them.
  const doLoginAs = async () => {
    if (!activeId) return
    try {
      const {data} = await loginAsCustomer(activeId)
      if (!data.ok || !data.api_token) {
        setError(data.error || 'כניסה לחשבון נכשלה')
        return
      }
      window.open(`/auth/login-as?token=${encodeURIComponent(data.api_token)}`, '_blank')
    } catch (e) {
      setError(err(e, 'כניסה לחשבון נכשלה'))
    }
  }

  const doOpenTicket = async () => {
    const subject = ticketSubject.trim()
    if (!activeId || !subject || creatingTicket) return
    setCreatingTicket(true)
    try {
      const {data} = await openTicket(activeId, {subject, description: ticketBody.trim()})
      if (!data.ok) {
        setError(data.error || 'פתיחת הפנייה נכשלה')
      } else {
        setTicketOpen(false)
        setTicketSubject('')
        setTicketBody('')
        setLastTicket(data.ticket.ticket_number)
        setError('')
      }
    } catch (e) {
      setError(err(e, 'פתיחת הפנייה נכשלה'))
    } finally {
      setCreatingTicket(false)
    }
  }

  const patchActive = async (patch: {name?: string; notes?: string; archived?: boolean; account?: string | null}) => {
    if (!activeId) return
    try {
      const {data} = await updateConversation(activeId, patch)
      setActive(data.conversation)
      setConvs((prev) => prev.map((c) => (c._id === activeId ? data.conversation : c)))
      setError('')
    } catch (e) {
      setError(err(e, 'העדכון נכשל'))
    }
  }

  // Date separators: a message opens a new day when its day differs from the
  // previous one's.
  const withDays = useMemo(() => {
    const out: {day?: string; msg: WaMessage}[] = []
    let prevDay = ''
    messages.forEach((m) => {
      const d = new Date(m.sent_at || m.created_at).toDateString()
      out.push({day: d !== prevDay ? dayLabel(m.sent_at || m.created_at) : undefined, msg: m})
      prevDay = d
    })
    return out
  }, [messages])

  const filters: {key: WaFilter; label: string}[] = [
    {key: 'all', label: 'הכל'},
    {key: 'unread', label: 'לא נקראו'},
    {key: 'unassigned', label: 'ללא שיוך'},
    {key: 'archived', label: 'בארכיון'},
  ]

  return (
    <div dir="rtl" style={{padding: 20}}>
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10}}>
        <h1 style={{margin: 0, fontSize: 24, fontWeight: 800}}>וואטסאפ</h1>
        <div style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'}}>
          {stats && (
            <>
              <span style={chip('#eef2f7', '#3f4254')}>שיחות: {stats.contacts}</span>
              <span style={chip(stats.unread ? '#25d366' : '#eef2f7', stats.unread ? '#fff' : '#3f4254')}>
                לא נקראו: {stats.unread}
              </span>
              <span style={chip('#eef2f7', '#3f4254')}>נכנסות 24ש: {stats.inbound_24h}</span>
              <span style={chip('#eef2f7', '#3f4254')}>יוצאות 24ש: {stats.outbound_24h}</span>
              <span style={chip('#eef2f7', '#3f4254')}>ללא שיוך: {stats.unassigned}</span>
            </>
          )}
          <button className="btn btn-sm btn-light" onClick={() => { loadList(); loadStats(); if (activeId) loadThread(activeId) }}>
            רענון
          </button>
        </div>
      </div>

      {!canSend && (
        <div className="alert alert-warning" style={{marginBottom: 12}}>
          שליחת הודעות מושבתת — חסרים פרטי ההתחברות של PayCall.
          יש למלא <code>PAYCALL_WA_URL</code>, <code>PAYCALL_WA_USER</code> ו־<code>PAYCALL_WA_PASSWORD</code> בקובץ ה־<code>.env</code>.
          קבלת הודעות עובדת ללא זה.
        </div>
      )}
      {error && (
        <div className="alert alert-danger" style={{marginBottom: 12}}>
          {error}
          <button className="btn btn-sm btn-light" style={{marginRight: 10}} onClick={() => setError('')}>סגור</button>
        </div>
      )}

      <div style={{display: 'flex', gap: 8, marginBottom: 14}}>
        <button className={`btn btn-sm ${tab === 'inbox' ? 'btn-primary' : 'btn-light'}`} onClick={() => setTab('inbox')}>
          שיחות
        </button>
        <button className={`btn btn-sm ${tab === 'contacts' ? 'btn-primary' : 'btn-light'}`} onClick={() => setTab('contacts')}>
          אנשי קשר
        </button>
      </div>

      {tab === 'inbox' ? (
        <div
          style={{
            display: 'flex',
            gap: 0,
            height: 'calc(100vh - 230px)',
            minHeight: 520,
            border: '1px solid #e6e9f0',
            borderRadius: 12,
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          {/* ---- conversation list ---- */}
          <div style={{width: 340, minWidth: 280, borderLeft: '1px solid #e6e9f0', display: 'flex', flexDirection: 'column'}}>
            <div style={{padding: 12, borderBottom: '1px solid #eef2f7'}}>
              <input
                className="form-control form-control-sm"
                placeholder="חיפוש לפי שם, טלפון או תוכן"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div style={{display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap'}}>
                {filters.map((f) => (
                  <button
                    key={f.key}
                    className={`btn btn-sm ${filter === f.key ? 'btn-primary' : 'btn-light'}`}
                    style={{padding: '2px 10px', fontSize: 12}}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{overflowY: 'auto', flex: 1}}>
              {loadingList && !convs.length && <div style={{padding: 16, color: '#7e8299'}}>טוען…</div>}
              {!loadingList && !convs.length && <div style={{padding: 16, color: '#7e8299'}}>אין שיחות להצגה</div>}
              {convs.map((c) => {
                const isActive = c._id === activeId
                return (
                  <div
                    key={c._id}
                    onClick={() => setActiveId(c._id)}
                    style={{
                      display: 'flex',
                      gap: 10,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f4f6fa',
                      background: isActive ? '#f0f2f5' : c.unread_count ? '#f7fffa' : '#fff',
                    }}
                  >
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: '50%',
                        background: '#25d366',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {initials(c)}
                    </div>
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', gap: 6}}>
                        <span style={{fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                          {c.name || prettyPhone(c.phone)}
                        </span>
                        <span style={{fontSize: 11, color: '#7e8299', whiteSpace: 'nowrap'}}>{listTime(c.last_message_at)}</span>
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: '#7e8299',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {c.last_direction === 'out' ? '↩ ' : ''}
                        {previewText(c.last_message_text) || '—'}
                      </div>
                      <div style={{display: 'flex', gap: 5, marginTop: 4, alignItems: 'center', flexWrap: 'wrap'}}>
                        {c.account && <span style={chip('#e8f5ff', '#0d6efd')}>{accountName(c.account)}</span>}
                        {c.code && <span style={chip('#f4f6fa', '#7e8299')}>{c.code}</span>}
                        {!!c.unread_count && <span style={chip('#25d366')}>{c.unread_count}</span>}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* ---- thread ---- (also the drop target for attachments) */}
          <div
            style={{flex: 1, display: 'flex', flexDirection: 'column', background: '#efeae2', minWidth: 0, position: 'relative'}}
            onDragEnter={(e) => {
              if (!active || !canSend || !Array.from(e.dataTransfer.types).includes('Files')) return
              e.preventDefault()
              setDragDepth((d) => d + 1)
            }}
            onDragOver={(e) => {
              if (!active || !canSend || !Array.from(e.dataTransfer.types).includes('Files')) return
              e.preventDefault() // required, or the browser opens the file instead of dropping it
              e.dataTransfer.dropEffect = 'copy'
            }}
            onDragLeave={() => setDragDepth((d) => Math.max(0, d - 1))}
            onDrop={(e) => {
              if (!active || !canSend) return
              e.preventDefault()
              setDragDepth(0)
              const f = e.dataTransfer.files && e.dataTransfer.files[0]
              if (f) setAttachFile(f)
            }}
          >
            {dragDepth > 0 && (
              <div
                style={{
                  position: 'absolute', inset: 8, zIndex: 20, pointerEvents: 'none',
                  border: '3px dashed #25d366', borderRadius: 14, background: 'rgba(37, 211, 102, 0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, fontWeight: 800, color: '#128c7e',
                }}
              >
                📎 שחררו כאן כדי לצרף לשיחה
              </div>
            )}
            {!active ? (
              <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7e8299'}}>
                בחר שיחה מהרשימה
              </div>
            ) : (
              <>
                <div
                  style={{
                    padding: '10px 16px',
                    background: '#f0f2f5',
                    borderBottom: '1px solid #e6e9f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontWeight: 800}}>{active.name || prettyPhone(active.phone)}</div>
                    <div style={{fontSize: 12, color: '#7e8299'}}>
                      {prettyPhone(active.phone)}
                      {active.account ? ` · ${accountName(active.account)}` : ' · ללא שיוך ללקוח'}
                      {active.account && active.account_source === 'phone' && (
                        <span title="הלקוח זוהה לפי מספר הטלפון. אם זה לא הוא — אפשר לשנות ב'פרטים'." style={{color: '#a1a5b7'}}>
                          {' '}(זוהה לפי טלפון)
                        </span>
                      )}
                      {active.code ? ` · קוד ${active.code}` : ''}
                    </div>
                  </div>
                  {active.account && (
                    <button className="btn btn-sm btn-light" onClick={doLoginAs} title="פתיחת חשבון הלקוח בלשונית חדשה">
                      כניסה לחשבון
                    </button>
                  )}
                  <button className="btn btn-sm btn-light" onClick={() => setTicketOpen((s) => !s)}>
                    פתח פנייה
                  </button>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => setAgentOpen((s) => !s)}
                    title="פעולות סוכן על חשבון הלקוח — ידני בלבד, כל פעולה נרשמת וניתנת לביטול"
                  >
                    🤖 סוכן{agentLog.filter((a) => a.status === 'applied').length ? ` (${agentLog.filter((a) => a.status === 'applied').length})` : ''}
                  </button>
                  <button className="btn btn-sm btn-light" onClick={() => setShowInfo((s) => !s)}>
                    {showInfo ? 'סגור פרטים' : 'פרטים'}
                  </button>
                  <button className="btn btn-sm btn-light" onClick={() => patchActive({archived: !active.archived})}>
                    {active.archived ? 'הוצא מארכיון' : 'לארכיון'}
                  </button>
                </div>

                {/* Customer state at a glance — is their setup actually running? */}
                {active.account && context && (
                  <div
                    style={{
                      background: '#fff',
                      borderBottom: '1px solid #e6e9f0',
                      padding: '8px 16px',
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      fontSize: 12,
                    }}
                  >
                    <span style={chip('#e8f5ff', '#0d6efd')}>{accountName(context.account)}</span>
                    {context.account?.phone && <span style={{color: '#7e8299'}} dir="ltr">{context.account.phone}</span>}
                    {context.stats && typeof context.stats.active_profiles === 'number' && (
                      <span style={chip(context.stats.active_profiles > 0 ? '#e7f8ee' : '#fdeaea', context.stats.active_profiles > 0 ? '#0a7a3d' : '#b42318')}>
                        פרופילים פעילים: {context.stats.active_profiles}
                        {typeof context.stats.profile_count === 'number' ? ` / ${context.stats.profile_count}` : ''}
                      </span>
                    )}
                    {context.stats && context.stats.disconnected_profiles > 0 && (
                      <span style={chip('#fdeaea', '#b42318')}>
                        מנותקים: {context.stats.disconnected_profiles}
                        {context.stats.disconnected_days ? ` (${context.stats.disconnected_days} ימים)` : ''}
                      </span>
                    )}
                    {context.stats && typeof context.stats.posted_today === 'number' && (
                      <span style={chip('#f4f6fa', '#3f4254')}>
                        פורסם היום: {context.stats.posted_today}
                        {typeof context.stats.scheduled_today === 'number' ? ` / ${context.stats.scheduled_today}` : ''}
                      </span>
                    )}
                    <span style={chip('#f4f6fa', '#3f4254')}>קמפיינים: {context.campaigns}</span>
                    <span style={chip(context.open_tickets ? '#fff4e5' : '#f4f6fa', context.open_tickets ? '#b54708' : '#3f4254')}>
                      פניות פתוחות: {context.open_tickets}
                    </span>
                  </div>
                )}

                {lastTicket && (
                  <div style={{background: '#e7f8ee', borderBottom: '1px solid #e6e9f0', padding: '6px 16px', fontSize: 12, color: '#0a7a3d'}}>
                    נפתחה פנייה {lastTicket}
                    <button className="btn btn-sm btn-light" style={{marginRight: 10, padding: '0 8px'}} onClick={() => setLastTicket('')}>
                      סגור
                    </button>
                  </div>
                )}

                {agentOpen && (
                  <div style={{background: '#fff', borderBottom: '1px solid #e6e9f0', padding: 14}}>
                    <div style={{fontWeight: 700, marginBottom: 4, fontSize: 13}}>פעולות סוכן על חשבון הלקוח</div>
                    <div style={{fontSize: 11, color: '#7e8299', marginBottom: 10}}>
                      ידני בלבד — שום פעולה לא רצה אוטומטית. כל פעולה נרשמת עם הערכים שהיו לפניה וניתנת לביטול.
                    </div>

                    {!active.account ? (
                      <div style={{fontSize: 12, color: '#b54708'}}>
                        השיחה אינה משויכת ללקוח — יש לשייך אותה ב"פרטים" כדי להפעיל פעולות.
                      </div>
                    ) : (
                      <>
                        <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center'}}>
                          <select
                            className="form-select form-select-sm"
                            style={{maxWidth: 260}}
                            value={agentAction}
                            onChange={(e) => {
                              setAgentAction(e.target.value)
                              setAgentTarget('')
                            }}
                          >
                            <option value="">בחר פעולה…</option>
                            {agentDefs.map((d) => (
                              <option key={d.key} value={d.key}>{d.label}</option>
                            ))}
                          </select>

                          {(() => {
                            const def = agentDefs.find((d) => d.key === agentAction)
                            if (!def || def.target === 'account') return null
                            const opts =
                              def.target === 'profile' ? context?.profile_list || [] : context?.campaign_list || []
                            return (
                              <select
                                className="form-select form-select-sm"
                                style={{maxWidth: 260}}
                                value={agentTarget}
                                onChange={(e) => setAgentTarget(e.target.value)}
                              >
                                <option value="">{def.target === 'profile' ? 'בחר פרופיל…' : 'בחר קמפיין…'}</option>
                                {opts.map((o: any) => (
                                  <option key={o._id} value={o._id}>{o.name}</option>
                                ))}
                              </select>
                            )
                          })()}

                          <button className="btn btn-sm btn-primary" disabled={!agentAction || agentBusy} onClick={doRunAgent}>
                            {agentBusy ? 'מבצע…' : 'הפעל'}
                          </button>
                        </div>

                        <div style={{marginTop: 12}}>
                          <div style={{fontWeight: 700, fontSize: 12, marginBottom: 6}}>היסטוריית פעולות</div>
                          {!agentLog.length && <div style={{fontSize: 12, color: '#7e8299'}}>עדיין לא בוצעו פעולות ללקוח זה</div>}
                          {agentLog.map((a) => (
                            <div
                              key={a._id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '6px 0',
                                borderBottom: '1px solid #f4f6fa',
                                fontSize: 12,
                              }}
                            >
                              <span style={{flex: 1}}>
                                {a.summary}
                                <span style={{color: '#a1a5b7'}}>
                                  {' · '}
                                  {a.triggered_by_name || '—'}
                                  {' · '}
                                  {listTime(a.created_at)}
                                </span>
                                {a.status === 'failed' && a.error && (
                                  <span style={{color: '#d92d20'}}> · {a.error}</span>
                                )}
                                {a.status !== 'failed' && (
                                  <span style={{display: 'block', color: '#7e8299', fontSize: 11}}>
                                    {changeLines(a.before, a.after, a.absent_fields).join(' · ')}
                                  </span>
                                )}
                              </span>
                              {a.status === 'applied' && (
                                <button className="btn btn-sm btn-light" style={{padding: '0 8px'}} disabled={agentBusy} onClick={() => doRevertAgent(a._id)}>
                                  בטל
                                </button>
                              )}
                              {a.status === 'reverted' && (
                                <span style={chip('#f4f6fa', '#7e8299')}>בוטל{a.reverted_by_name ? ' · ' + a.reverted_by_name : ''}</span>
                              )}
                              {a.status === 'failed' && <span style={chip('#fdeaea', '#b42318')}>נכשל</span>}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {ticketOpen && (
                  <div style={{background: '#fff', borderBottom: '1px solid #e6e9f0', padding: 14}}>
                    <div style={{fontWeight: 700, marginBottom: 8, fontSize: 13}}>פתיחת פנייה מתוך השיחה</div>
                    <input
                      className="form-control form-control-sm"
                      placeholder="כותרת הפנייה"
                      value={ticketSubject}
                      onChange={(e) => setTicketSubject(e.target.value)}
                    />
                    <textarea
                      className="form-control form-control-sm"
                      style={{marginTop: 8}}
                      rows={3}
                      placeholder="פרטים (10 ההודעות האחרונות יצורפו אוטומטית)"
                      value={ticketBody}
                      onChange={(e) => setTicketBody(e.target.value)}
                    />
                    <div style={{display: 'flex', gap: 8, marginTop: 8}}>
                      <button className="btn btn-sm btn-primary" disabled={!ticketSubject.trim() || creatingTicket} onClick={doOpenTicket}>
                        {creatingTicket ? 'פותח…' : 'פתח פנייה'}
                      </button>
                      <button className="btn btn-sm btn-light" onClick={() => setTicketOpen(false)}>ביטול</button>
                    </div>
                  </div>
                )}

                {showInfo && (
                  <div style={{background: '#fff', borderBottom: '1px solid #e6e9f0', padding: 14}}>
                    <div style={{display: 'flex', gap: 14, flexWrap: 'wrap'}}>
                      <div style={{flex: '1 1 260px'}}>
                        <label className="form-label" style={{fontSize: 12, fontWeight: 700}}>שם</label>
                        <input
                          className="form-control form-control-sm"
                          defaultValue={active.name}
                          onBlur={(e) => e.target.value !== active.name && patchActive({name: e.target.value})}
                        />
                        <label className="form-label" style={{fontSize: 12, fontWeight: 700, marginTop: 10}}>לקוח משויך</label>
                        {active.account ? (
                          <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                            <span style={chip('#e8f5ff', '#0d6efd')}>{accountName(active.account)}</span>
                            <button className="btn btn-sm btn-light" onClick={() => patchActive({account: null})}>בטל שיוך</button>
                          </div>
                        ) : (
                          <>
                            <input
                              className="form-control form-control-sm"
                              placeholder="חיפוש לקוח לשיוך…"
                              value={acctQuery}
                              onChange={(e) => setAcctQuery(e.target.value)}
                            />
                            {!!acctResults.length && (
                              <div style={{border: '1px solid #e6e9f0', borderRadius: 8, marginTop: 6, maxHeight: 160, overflowY: 'auto'}}>
                                {acctResults.map((a) => (
                                  <div
                                    key={a._id}
                                    style={{padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid #f4f6fa'}}
                                    onClick={() => {
                                      patchActive({account: a._id})
                                      setAcctQuery('')
                                      setAcctResults([])
                                    }}
                                  >
                                    <div style={{fontWeight: 600}}>{accountName(a)}</div>
                                    <div style={{fontSize: 11, color: '#7e8299'}}>{a.phone || a.email}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div style={{flex: '1 1 260px'}}>
                        <label className="form-label" style={{fontSize: 12, fontWeight: 700}}>הערות</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={4}
                          value={notesDraft}
                          onChange={(e) => setNotesDraft(e.target.value)}
                        />
                        <button
                          className="btn btn-sm btn-primary"
                          style={{marginTop: 6}}
                          onClick={() => patchActive({notes: notesDraft})}
                        >
                          שמירת הערות
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{flex: 1, overflowY: 'auto', padding: '14px 18px'}}>
                  {loadingThread && !messages.length && <div style={{color: '#7e8299'}}>טוען…</div>}
                  {!loadingThread && !messages.length && <div style={{color: '#7e8299'}}>אין הודעות בשיחה</div>}
                  {withDays.map(({day, msg: m}) => (
                    <div key={m._id}>
                      {day && (
                        <div style={{textAlign: 'center', margin: '12px 0'}}>
                          <span style={{...chip('#ffffff', '#54656f'), boxShadow: '0 1px 1px rgba(0,0,0,.08)'}}>{day}</span>
                        </div>
                      )}
                      <div style={{display: 'flex', justifyContent: m.direction === 'in' ? 'flex-start' : 'flex-end', marginBottom: 6}}>
                        <div
                          style={{
                            maxWidth: '72%',
                            background: m.direction === 'in' ? '#fff' : '#d9fdd3',
                            borderRadius: 10,
                            padding: '7px 10px',
                            boxShadow: '0 1px 1px rgba(0,0,0,.09)',
                            border: m.status === 'failed' ? '1px solid #f1aeb5' : 'none',
                          }}
                        >
                          {m.media?.url && (
                            <div style={{marginBottom: 6}}>
                              {/* Served through the admin media proxy — the provider's own
                                  media URL answers 401 without the API token. */}
                              {m.type === 'image' ? (
                                <a href={mediaUrl(m._id)} target="_blank" rel="noreferrer">
                                  <img
                                    src={mediaUrl(m._id)}
                                    alt={m.media.caption || 'תמונה'}
                                    style={{maxWidth: 260, maxHeight: 260, borderRadius: 6, display: 'block'}}
                                  />
                                </a>
                              ) : m.type === 'video' ? (
                                <video src={mediaUrl(m._id)} controls style={{maxWidth: 260, borderRadius: 6}} />
                              ) : m.type === 'audio' ? (
                                <audio src={mediaUrl(m._id)} controls style={{maxWidth: 260}} />
                              ) : (
                                <a href={mediaUrl(m._id)} target="_blank" rel="noreferrer" style={{fontSize: 12}}>
                                  📎 {m.media.filename || m.type}
                                </a>
                              )}
                            </div>
                          )}
                          {m.type === 'contacts' && parseSharedContacts(m.body).length > 0 ? (
                            parseSharedContacts(m.body).map((sc, i) => (
                              <div
                                key={i}
                                style={{display: 'flex', gap: 10, alignItems: 'center', padding: '4px 2px', minWidth: 200}}
                              >
                                <div
                                  style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    background: '#dfe5e7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 18,
                                    flexShrink: 0,
                                  }}
                                >
                                  👤
                                </div>
                                <div style={{minWidth: 0}}>
                                  <div style={{fontWeight: 600}}>{sc.name}</div>
                                  {sc.phones.map((ph, j) => {
                                    const digits = String(ph.wa_id || ph.phone).replace(/\D/g, '')
                                    return (
                                      <div key={j} style={{fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'}}>
                                        <a href={`tel:+${digits}`} dir='ltr'>
                                          {ph.phone || prettyPhone(digits)}
                                        </a>
                                        {ph.type && <span style={{color: '#8696a0', fontSize: 11}}>{ph.type}</span>}
                                        {ph.wa_id && (
                                          <a href={`https://wa.me/${ph.wa_id}`} target='_blank' rel='noreferrer' style={{fontSize: 11}}>
                                            WhatsApp
                                          </a>
                                        )}
                                        <button
                                          type='button'
                                          className='btn btn-link p-0'
                                          style={{fontSize: 11}}
                                          onClick={() => navigator.clipboard?.writeText(digits)}
                                        >
                                          העתק
                                        </button>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                              {m.body || (m.type !== 'text' ? `[${m.type}]` : '')}
                            </div>
                          )}
                          <div style={{display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center', marginTop: 2}}>
                            {m.sent_by_name && <span style={{fontSize: 10, color: '#8696a0'}}>{m.sent_by_name}</span>}
                            <span style={{fontSize: 10, color: '#8696a0'}}>{bubbleTime(m.sent_at || m.created_at)}</span>
                            {m.direction === 'out' && (
                              <span style={{fontSize: 11, color: statusColor(m)}} title={m.error || m.status}>
                                {statusMark(m)}
                              </span>
                            )}
                          </div>
                          {m.status === 'failed' && m.error && (
                            <div style={{fontSize: 11, color: '#d92d20', marginTop: 3}}>{m.error}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>

                <div style={{padding: 10, background: '#f0f2f5', borderTop: '1px solid #e6e9f0'}}>
                  {!active.window_open && (
                    <div style={{fontSize: 12, color: '#b54708', marginBottom: 6}}>
                      חלון 24 השעות נסגר — מטא לא תעביר הודעת טקסט חופשי עד שהלקוח יכתוב שוב. נדרשת תבנית מאושרת.
                    </div>
                  )}

                  {/* AI drafts. Clicking one only fills the box — sending stays a
                      separate, deliberate click. */}
                  {suggestedAction && (
                    <div
                      style={{
                        marginBottom: 8,
                        padding: '10px 12px',
                        background: '#fff',
                        border: '1px solid #c7d7fe',
                        borderRight: '4px solid #1877f2',
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    >
                      <div style={{fontWeight: 700, fontSize: 13, marginBottom: 2}}>
                        🤖 פעולה מוצעת: {suggestedAction.label}
                        {suggestedAction.target_label ? ` — ${suggestedAction.target_label}` : ''}
                      </div>
                      {suggestedAction.reason && <div style={{color: '#5e6278', marginBottom: 6}}>{suggestedAction.reason}</div>}
                      <div style={{background: '#f5f8fa', borderRadius: 6, padding: '6px 10px', marginBottom: 8}}>
                        <div style={{fontSize: 11, color: '#7e8299', marginBottom: 2}}>השינוי שיבוצע (לפני ← אחרי):</div>
                        {suggestedAction.changes.map((c) => (
                          <div key={c.field} style={{fontWeight: 600}}>
                            {FIELD_LABELS[c.field] || c.field}: {fmtVal(c.before)} ← {fmtVal(c.after)}
                          </div>
                        ))}
                      </div>
                      <div style={{display: 'flex', gap: 6, alignItems: 'center'}}>
                        <button className="btn btn-sm btn-primary" disabled={agentBusy} onClick={doApplySuggestedAction}>
                          {agentBusy ? 'מבצע…' : 'אשר והפעל'}
                        </button>
                        <button className="btn btn-sm btn-light" disabled={agentBusy} onClick={() => setSuggestedAction(null)}>
                          התעלם
                        </button>
                        <span style={{color: '#a1a5b7', fontSize: 11}}>נרשם ביומן הפעולות וניתן לביטול</span>
                      </div>
                    </div>
                  )}

                  {suggesting && !suggestions.length && (
                    <div style={{fontSize: 12, color: '#7e8299', marginBottom: 6}}>✨ מכין הצעות תשובה…</div>
                  )}

                  {(suggestions.length > 0 || aiSummary) && (
                    <div style={{marginBottom: 8}}>
                      {aiSummary && (
                        <div style={{fontSize: 12, color: '#7e8299', marginBottom: 6}}>
                          <b>סיכום:</b> {aiSummary}
                        </div>
                      )}
                      {aiDiagnosis && (
                        <div
                          style={{fontSize: 12, color: '#5e4200', background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: 6, padding: '6px 10px', marginBottom: 6}}
                          title="מבוסס על נתוני החשבון: פרופילים, תזמונים ושגיאות, קמפיינים ומנוי"
                        >
                          <b>🔍 אבחון (פנימי — לא נשלח ללקוח):</b> {aiDiagnosis}
                        </div>
                      )}
                      <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                        {suggestions.map((s, i) => (
                          <button
                            key={i}
                            className="btn btn-sm btn-light"
                            style={{
                              textAlign: 'right',
                              whiteSpace: 'normal',
                              maxWidth: 420,
                              fontSize: 12,
                              border: '1px solid #d7dbe6',
                            }}
                            title={`לחיצה (או Alt+${i + 1}) תמלא את תיבת הכתיבה — השליחה נשארת בידיים שלך`}
                            onClick={() => setDraft(s)}
                          >
                            <span style={{...chip('#e8f0fe', '#1877f2'), marginLeft: 6, padding: '0 7px'}}>{i + 1}</span>
                            {s}
                          </button>
                        ))}
                        <button className="btn btn-sm btn-light" style={{fontSize: 12}} onClick={() => { setSuggestions([]); setAiSummary(''); setAiDiagnosis(''); setSuggestedAction(null) }}>
                          נקה
                        </button>
                      </div>
                    </div>
                  )}

                  {attachFile && (
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #d7dbe6', borderRadius: 8, padding: '6px 10px', marginBottom: 8, fontSize: 12}}>
                      {attachPreview ? (
                        <img src={attachPreview} alt="" style={{width: 40, height: 40, objectFit: 'cover', borderRadius: 4}} />
                      ) : (
                        <span style={{fontSize: 22}}>📎</span>
                      )}
                      <span style={{flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {attachFile.name} · {(attachFile.size / 1024 / 1024).toFixed(1)}MB
                        <span style={{color: '#a1a5b7'}}> — הטקסט בתיבה יישלח ככיתוב</span>
                      </span>
                      <button className="btn btn-sm btn-light" style={{padding: '0 8px'}} onClick={() => setAttachFile(null)} title="הסר קובץ">✕</button>
                    </div>
                  )}

                  <div style={{display: 'flex', gap: 8}}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      style={{display: 'none'}}
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0]
                        if (f) setAttachFile(f)
                        e.target.value = ''
                      }}
                    />
                    <button
                      className="btn btn-light"
                      disabled={!canSend || sending}
                      onClick={() => fileInputRef.current?.click()}
                      title="צירוף קובץ: תמונה (JPG/PNG עד 5MB), וידאו/אודיו (עד 16MB), מסמך (עד 100MB)"
                    >
                      📎
                    </button>
                    <button
                      className="btn btn-light"
                      disabled={suggesting || !messages.length}
                      onClick={doSuggest}
                      title="הצעות תשובה מ-AI לפי השיחה ונתוני הלקוח. לא נשלח כלום אוטומטית."
                    >
                      {suggesting ? 'חושב…' : '✨ הצע תשובה'}
                    </button>
                    <textarea
                      className="form-control"
                      rows={1}
                      style={{resize: 'none'}}
                      placeholder={canSend ? 'כתוב הודעה…' : 'שליחה מושבתת — חסרים פרטי PayCall'}
                      value={draft}
                      disabled={!canSend || sending}
                      onChange={(e) => setDraft(e.target.value)}
                      onPaste={(e) => {
                        // A pasted screenshot/file becomes the attachment; pasted text stays text.
                        const f = Array.from(e.clipboardData.files || [])[0]
                        if (f) {
                          e.preventDefault()
                          setAttachFile(f)
                        }
                      }}
                      onKeyDown={(e) => {
                        // Enter sends, Shift+Enter breaks a line — chat convention.
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          doSend()
                        }
                      }}
                    />
                    <button className="btn btn-primary" disabled={!canSend || sending || (!draft.trim() && !attachFile)} onClick={doSend}>
                      {sending ? 'שולח…' : 'שלח'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div style={card}>
          <div style={{display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12}}>
            <input
              className="form-control form-control-sm"
              style={{maxWidth: 320}}
              placeholder="חיפוש איש קשר"
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
            />
            <span style={{color: '#7e8299', fontSize: 13}}>{contacts.length} אנשי קשר</span>
          </div>
          <div style={{overflowX: 'auto'}}>
            <table className="table table-row-bordered align-middle">
              <thead>
                <tr style={{fontWeight: 700, fontSize: 13}}>
                  <th>שם</th>
                  <th>טלפון</th>
                  <th>לקוח משויך</th>
                  <th>קוד</th>
                  <th>הודעות</th>
                  <th>לא נקראו</th>
                  <th>פעילות אחרונה</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loadingContacts && (
                  <tr><td colSpan={8} style={{color: '#7e8299'}}>טוען…</td></tr>
                )}
                {!loadingContacts && !contacts.length && (
                  <tr><td colSpan={8} style={{color: '#7e8299'}}>אין אנשי קשר</td></tr>
                )}
                {contacts.map((c) => (
                  <tr key={c._id}>
                    <td style={{fontWeight: 600}}>{c.name || '—'}</td>
                    <td dir="ltr" style={{textAlign: 'right'}}>{prettyPhone(c.phone)}</td>
                    <td>{c.account ? <span style={chip('#e8f5ff', '#0d6efd')}>{accountName(c.account)}</span> : <span style={{color: '#a1a5b7'}}>—</span>}</td>
                    <td>{c.code || '—'}</td>
                    <td>{c.message_count}</td>
                    <td>{c.unread_count ? <span style={chip('#25d366')}>{c.unread_count}</span> : '—'}</td>
                    <td style={{fontSize: 13, color: '#7e8299'}}>{listTime(c.last_message_at)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => {
                          setTab('inbox')
                          setActiveId(c._id)
                        }}
                      >
                        פתח שיחה
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default WhatsappPage
